import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarView } from "./models/CalendarView";
import { addDays, getMonthGridDays, parseYmd, startOfWeek, toYmd, WeekStartDay } from "./utils/calendar-range";

export interface CalendarViewState {
  /** The day the view is centred on. */
  anchor: Date;
  view: CalendarView;
  /** Inclusive YYYY-MM-DD range the visible grid covers. */
  from: string;
  to: string;
  /** Localized heading for the visible range ("Aug 10 – Aug 16, 2026" / "August 2026"). */
  rangeLabel: string;
  setView: (nextView: CalendarView) => void;
  /** Moves one week / one month in the given direction. */
  step: (direction: 1 | -1) => void;
  goToToday: () => void;
  /** Switches to the week containing the given day. */
  drillDownToWeek: (day: Date) => void;
}

/**
 * Keeps the viewed range in the URL (?date=YYYY-MM-DD&view=month) so leaving the
 * page and coming back — or sharing the link — restores the same view. Global
 * search deep-links here with ?date=YYYY-MM-DD and gets the same treatment.
 */
export const useCalendarViewState = (weekStart: WeekStartDay = "Monday"): CalendarViewState => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const view: CalendarView = searchParams.get("view") === "month" ? "month" : "week";

  const anchor = useMemo(() => {
    if (dateParam) {
      const parsed = parseYmd(dateParam);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return new Date();
  }, [dateParam]);

  // Replace (don't stack) history entries so one Back press leaves the calendar,
  // and returning restores the last-viewed range.
  const updateView = (date: Date, nextView: CalendarView = view) => {
    const next = new URLSearchParams(searchParams);
    next.set("date", toYmd(date));
    if (nextView === "month") {
      next.set("view", "month");
    } else {
      next.delete("view");
    }
    setSearchParams(next, { replace: true });
  };

  const { from, to } = useMemo(() => {
    if (view === "week") {
      const weekFirst = startOfWeek(anchor, weekStart);
      return { from: toYmd(weekFirst), to: toYmd(addDays(weekFirst, 6)) };
    }
    const grid = getMonthGridDays(anchor, weekStart);
    return { from: toYmd(grid[0]), to: toYmd(grid[grid.length - 1]) };
  }, [view, anchor, weekStart]);

  const rangeLabel = useMemo(() => {
    if (view === "month") {
      return new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" }).format(anchor);
    }
    const weekFirst = startOfWeek(anchor, weekStart);
    const weekLast = addDays(weekFirst, 6);
    const dayFormat = new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" });
    const yearFormat = new Intl.DateTimeFormat(i18n.language, { year: "numeric" });
    return `${dayFormat.format(weekFirst)} – ${dayFormat.format(weekLast)}, ${yearFormat.format(weekLast)}`;
  }, [view, anchor, i18n.language, weekStart]);

  return {
    anchor,
    view,
    from,
    to,
    rangeLabel,
    setView: (nextView) => updateView(anchor, nextView),
    step: (direction) =>
      updateView(
        view === "week"
          ? addDays(anchor, direction * 7)
          : new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1)
      ),
    goToToday: () => updateView(new Date()),
    drillDownToWeek: (day) => updateView(day, "week"),
  };
};
